<template>

  <!-- Mandatory graphic print options -->
  <div v-if="mandatoryCount > 0" class="my-4">
    <h4 class="mt-4">
        {{ $t('configurator.headings.lettering_required') }}
    </h4>

    <div v-if="!mandatorySelected">
      <div class="text-center mt-3">
        <button
          type="button"
          class="btn btn-primary"
          :disabled="availableRefinements.length === 0"
          @click="showAddMandatoryModal">
        {{ $t('configurator.buttons.add_lettering') }}
        </button>
      </div>
    </div>
    <div v-else style="display: flex; flex-direction: column;">
      
      <div
        v-for="entry in selectedMandatoryConfigurationEntries"
        :key="entry.id"
        class="article-row"
      >
        <div class="article-frame col-12">
          <configurator-mandatory-booth-refinement-entry
            :configuration="entry"
          />
        </div>
      </div>
    </div>
  </div>

  <!-- additional graphic print options -->
  <div v-if="mandatorySelected">
    <h4 class="mt-4" title="Grafische Gestaltung">
      {{ $t('configurator.headings.graphic_design') }}
    </h4>

    <div style="display: flex; flex-direction: column;">

      <template
        v-for="refinement in availableRefinements"
        :key="refinement.id"
      >
        <div
          v-for="article in refinement.articles"
          :key="refinement.id + '-' + article.id"
          class="article-row"
        >
          <div
            v-if="visibleAvailableRefinementArticles.includes(refinement.id + '-' + article.id) && !article.is_seamless_print"
            class="article-frame col-12"
          >
            <configurator-optional-booth-refinement-entry
              :refinement="refinement"
              :article="article"
              @add="handleAddOptionalRefinement"
              @remove="handleRemoveOptionalRefinement"
              @update="handleUpdateOptionalRefinement"
            />
          </div>
        </div>
      </template>
    </div>
  </div>

  <!-- Modals -->
  <!-- Select mandatory refinement -->
  <AddMandatoryBoothRefinementModal v-if="addMandatoryModalActive"
    :form="refinementForm"
    :available-refinements="availableRefinements"
    @close="hideAddMandatoryModal"
    @submit="addMandatoryRefinements"
  />
</template>

<script setup>
import { computed, reactive, ref } from 'vue'
import { useMainStore } from '@/store'
import Form from '@/classes/Form'
import { useI18n } from 'vue-i18n'
import moment from 'moment'
import { filterAvailableRefinements } from '@/utils/boothRefinements'
import AddMandatoryBoothRefinementModal from '@/components_new/Configurator/AddMandatoryBoothRefinementModal.vue'
import { ARTICLE_TYPE_PRINT } from '@/constants'

const { t } = useI18n({ useScope: 'global' })
const store = useMainStore()
// define props
const props = defineProps({
  mandatorySelected: {
    type: Boolean,
    default: false
  },
  mandatoryCount: {
    type: Number,
    default: 0
  },
  selectedMandatoryConfigurationEntries: {
    type: Array,
    default: []
  },
})
// define reactive props
const addMandatoryModalActive = ref(false)
const mandatoryMode = ref(false)
const refinementForm = reactive(new Form({
  refinement: null, // deprecated
  refinements: [],
  is_logo_print: false,
  spanning_mode: null,
  label: '',
  article: null,
  file: null,
  is_seamless_print: false,
}))

// define computed props
const basket = computed(() => store.settings.basket)
const shoppingCart = computed(() => store.settings.shopping_cart)
const disabledBoothRefinementArticleIds = computed(() => store.availability.disabled_booth_refinement_ids)
const bookableArticleIds = computed(() => store.availability.bookable_article_ids)
const includedCharacters = computed(() => store.tmp.characters_inclusive)

const availableRefinements = computed(() => {
  return filterAvailableRefinements(
		shoppingCart.value,
		disabledBoothRefinementArticleIds.value,
		mandatoryMode.value,
    bookableArticleIds.value
	)
})

// Compute which article is visible for which refinement
const visibleAvailableRefinementArticles = computed(() => {
  const seenArticleIds = []
  const result = []
  availableRefinements.value.forEach((refinement) => {
    refinement.articles.forEach((article) => {
      if (!seenArticleIds.includes(article.article_id)) {
        seenArticleIds.push(article.article_id)
        result.push(refinement.id + '-' + article.id)
      }
    })
  })
  return result
})

const addRefinement = (refinement, form, rIndex, potentialGroup, totalArea = 0, refinementsCount = 1) => {
  const clonedRefinement = _.cloneDeep(refinement)
  // spanning.value is not a reference, it's just a boolean value
  const group = (form.spanning_mode && form.spanning_mode.value) ? potentialGroup : clonedRefinement.id
  const configuration = {
    id: clonedRefinement.id,
    booth_refinement: clonedRefinement,
    is_logo_print: form.is_logo_print,
    is_seamless_print: form.is_seamless_print,
    article_id: form.article.article.id,
    label: form.label,
    file: form.file,
    group: (form.is_seamless_print) ? group : clonedRefinement.id
  }
  shoppingCart.value.addBoothConfiguration(configuration)

  // find print article 
  const index = basket.value.selectedArticles.findIndex((article) => {
    return article.id === configuration.article_id
  })
  if (index === -1) {
    console.warn('lettering article not found')
    return
  }
  const clonedArticle = _.cloneDeep(basket.value.selectedArticles[index])
  if (configuration.is_logo_print) {
    clonedArticle.count = 1
  } else {
    if (form.spanning_mode && form.spanning_mode.value && rIndex > 0) {
      clonedArticle.count = 0
      clonedArticle.count_after_inclusive = 0
      clonedArticle.inclusive = 0
    } else {
      clonedArticle.count = configuration.label
        .trim()
        .replace(/ /g, '')
        .length
      if (clonedArticle.count <= includedCharacters.value) {
        clonedArticle.inclusive = clonedArticle.count
      } else {
        clonedArticle.inclusive = includedCharacters.value
      }
      clonedArticle.count_after_inclusive = clonedArticle.count - clonedArticle.inclusive
    }
  }

  if(form.article.is_included) {
    clonedArticle.print_inclusive = 1
    clonedArticle.inclusive = 1
    clonedArticle.price = 0
    clonedArticle.total = 0
    clonedArticle.totalNet = 0
    clonedArticle.totalVat = 0
    clonedArticle.name = clonedArticle.name + ' (inklusive)'
    clonedArticle.name_en = clonedArticle.name_en + ' (inclusive)'
  }
  shoppingCart.value.addBoothRefinementArticle(clonedArticle, clonedRefinement)
}

const handleAddOptionalRefinement = (refinement, article) => {
  const clonedRefinement = _.cloneDeep(refinement)
  
  // find print article 
  const index = basket.value.selectedArticles.findIndex((a) => {
    return a.id === article.article.id
  })
  if (index === -1) {
    console.warn('lettering article not found')
    return
  }
  const clonedArticle = _.cloneDeep(basket.value.selectedArticles[index])

  /*
  const configuration = {
    id: clonedRefinement.id,
    booth_refinement: clonedRefinement,
    is_logo_print: clonedArticle.type === ARTICLE_TYPE_PRINT,
    is_seamless_print: false,
    article_id: clonedArticle.id,
    label: null,
    file: null,
    group: clonedRefinement.id
  }
  console.log("Adding configuration ", configuration)
  shoppingCart.value.addBoothConfiguration(configuration)
  */

  // TODO: count remaining included characters

  // TODO: inclusive check
  clonedArticle.count = 1
  clonedArticle.count_after_inclusive = 0
  clonedArticle.inclusive = 0

  shoppingCart.value.addBoothRefinementArticle(clonedArticle, clonedRefinement)
}

/**
 * Handles decrementing (and removing if count after decrement is <= 0) the article for the given 
 * refinement in the shopping cart
 * 
 * @param refinement the refinement
 * @param article the article
 */
const handleRemoveOptionalRefinement = (refinement, article) => {
  shoppingCart.value.decreaseBoothRefinementArticle(refinement.id, article.article.id)
}

/**
 * Handles updating the count of an article for a given refinement in the shopping cart
 * 
 * @param refinement the refinement
 * @param article the article
 * @param count the updated count
 */
const handleUpdateOptionalRefinement = (refinement, article, count) => {
  console.info("handling update", {count: count, refinement: refinement.id, article:article.article.id})
  // Check if the article already exists in shopping cart, if not, create it first
  const index = shoppingCart.value.print_items.findIndex((printItem) => 
    printItem.booth_refinement_id === refinement.id && printItem.id === article.article.id
  )
  if (count > 0 && index === -1) {
    console.log("Adding initial refinment entry")
    handleAddOptionalRefinement(refinement, article);
  }

  // Update the count, removing, if 0
  shoppingCart.value.updateBoothRefinementArticleCount(refinement.id, article.article.id, count)
}

const showAddMandatoryModal = () => {
  mandatoryMode.value = true
  addMandatoryModalActive.value = true
}

const hideAddMandatoryModal = () => {
  addMandatoryModalActive.value = false
  mandatoryMode.value = false
}

const addMandatoryRefinements = async (forms) => {
  let index = 0
  const group = moment().unix()
  await forms.forEach((form) => {
    addRefinement(form.refinements[0], form, index, group)
    index++
  })
  
  hideAddMandatoryModal()
}
</script>